import { PlatformLocation } from '@angular/common';
import { HttpHeaders, HttpProgressEvent } from '@angular/common/http';
import { Params } from '@angular/router';
import { logRequest } from '@office-start/telemetry-service';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { HttpStatusCode } from '../constants/http-status-code.enum';
import { InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpDataResponse, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { ErrorDialogErrorType } from '../models/error-dialog.model';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { ContextService } from '../shared-context/context.service';
import { PromiseCancellationToken } from '../utilities/promise-cancellation-token.utility';
import { UrlParser } from '../utilities/url-parser.utility';
import { AccessTokenConsumer, AccessTokenService, CAPDialogData } from './access-token.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';

export class XhrServiceError<T> {
    constructor(public httpResponse: InstrumentedHttpDataResponse<T>, public clientError?: string) { }
}

/**
 * Abstract service that requests an API.
 */
export abstract class XhrService {
    private readonly retryIntervalsInMs = [0, 11, 22, 33];

    protected queryParams: Params;
    protected inMockMode: boolean;
    protected mockRequestHeader: string;

    constructor(
        protected accessTokenService: AccessTokenService,
        protected instrumentationService: InstrumentationService,
        protected instrumentedHttp: InstrumentedHttp,
        private readonly tokenConsumer: AccessTokenConsumer,
        protected contextService: ContextService,
        private readonly officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        protected readonly scheduler: SchedulerService,
        private readonly rpsCompactAuthHeaderPrefix: string = ''
    ) {
        this.inMockMode = contextService.sharedContext.inMockMode;
        this.mockRequestHeader = officeHomeApiConfigService.config.mockRequestHeader;
        this.queryParams = UrlParser.getQueryParams(platformLocation.search);
    }

    public static getErrorTypeFromServiceError(error: XhrServiceError<any>): ErrorDialogErrorType {
        const errorCode = error && error.httpResponse && error.httpResponse.status;
        switch (errorCode) {
            case HttpStatusCode.OK:
            case HttpStatusCode.Created:
            case HttpStatusCode.NoContent:
                return ErrorDialogErrorType.None;
            case HttpStatusCode.Unauthorized:
            case HttpStatusCode.Forbidden:
            case HttpStatusCode.TokenRefreshFailed:
            case HttpStatusCode.TokenUnavailable:
                return ErrorDialogErrorType.Unauthorized;
            case HttpStatusCode.NotFound:
                return ErrorDialogErrorType.NotFound;
            case HttpStatusCode.RequestTimeout:
            case HttpStatusCode.GatewayTimeout:
            case HttpStatusCode.Timeout:
                return ErrorDialogErrorType.Timeout;
            case HttpStatusCode.Offline:
                return ErrorDialogErrorType.Offline;
        }
        return ErrorDialogErrorType.Generic;
    }

    protected handleQueryParam(_paramNameInLowerCase: string, _value: string | Array<string>, _requestConfig?: HttpRequestOptions) {
        // Can be overridden by sub-classes
    }

    protected get<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        requestConfig: HttpRequestOptions,
        strategy: FetchStrategy,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        tokenConsumerOverride?: AccessTokenConsumer,
        resourceUrl?: string,
        schedulePriority = SchedulePriority.Immediate
    ) {
        // If cached data is requested then no token is needed
        if (strategy === FetchStrategy.CacheOnly) {
            tokenConsumerOverride = AccessTokenConsumer.None;
        }

        this.scheduler.scheduleWork(() => this.request(
            config => this.instrumentedHttp.get<T>(eventName, otelEventName, url, config, strategy, onDownloadProgress, cancellationToken, timeoutInMs),
            eventName,
            otelEventName,
            callback,
            errorCallback,
            requestConfig,
            tokenConsumerOverride,
            resourceUrl,
            null,
            strategy === FetchStrategy.CacheOnly
        ), schedulePriority);
    }

    protected getFromCache<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        requestConfig: HttpRequestOptions = {}
    ) {
        this.get(
            eventName,
            otelEventName,
            url,
            callback,
            errorCallback,
            requestConfig,
            FetchStrategy.CacheOnly,
            null,
            null,
            null,
            AccessTokenConsumer.None);
    }

    protected post<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        requestConfig: HttpRequestOptions = {},
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        tokenConsumerOverride?: AccessTokenConsumer,
        resourceUrl?: string,
        updateRequestBody?: (reqeustConfig: HttpRequestOptions, tokenResponse: AccessTokenResponse) => void,
        strategy?: FetchStrategy,
        interactive = false,
        capDialogData: CAPDialogData = null,
        schedulePriority: SchedulePriority = SchedulePriority.Immediate
    ) {
        // If cached data is requested then no token is needed
        if (strategy === FetchStrategy.CacheOnly) {
            tokenConsumerOverride = AccessTokenConsumer.None;
        }

        this.scheduler.scheduleWork(() => this.request(
            config => this.instrumentedHttp.post<T>(eventName, otelEventName, url, data, config, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs, strategy),
            eventName,
            otelEventName,
            callback,
            errorCallback,
            requestConfig,
            tokenConsumerOverride,
            resourceUrl,
            updateRequestBody,
            false,
            interactive,
            capDialogData
        ), schedulePriority);
    }

    protected put<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        requestConfig: HttpRequestOptions = {},
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        tokenConsumerOverride?: AccessTokenConsumer,
        resourceUrl?: string
    ) {
        this.request(
            config => this.instrumentedHttp.put<T>(eventName, otelEventName, url, data, config, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs),
            eventName,
            otelEventName,
            callback,
            errorCallback,
            requestConfig,
            tokenConsumerOverride,
            resourceUrl);
    }

    protected delete<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        requestConfig: HttpRequestOptions = {},
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        tokenConsumerOverride?: AccessTokenConsumer,
        resourceUrl?: string
    ) {
        this.request(
            config => this.instrumentedHttp.delete<T>(eventName, otelEventName, url, data, config, cancellationToken, timeoutInMs),
            eventName,
            otelEventName,
            callback,
            errorCallback,
            requestConfig,
            tokenConsumerOverride,
            resourceUrl);
    }

    protected patch<T>(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        url: string,
        data: any,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        requestConfig: HttpRequestOptions = {},
        onUploadProgress?: (progressEvent: HttpProgressEvent) => void,
        onDownloadProgress?: (progressEvent: HttpProgressEvent) => void,
        cancellationToken?: PromiseCancellationToken,
        timeoutInMs?: number,
        tokenConsumerOverride?: AccessTokenConsumer,
        resourceUrl?: string
    ) {
        this.request(
            config => this.instrumentedHttp.patch<T>(eventName, otelEventName, url, data, config, onUploadProgress, onDownloadProgress, cancellationToken, timeoutInMs),
            eventName,
            otelEventName,
            callback,
            errorCallback,
            requestConfig,
            tokenConsumerOverride,
            resourceUrl);
    }

    protected isAuthError(status: number, tokenType: AccessTokenConsumer): boolean {
        // For HWA, office.com APIs return a 403 if token is expired. This 403 check ensures we fetch a new token from WAM in that case.
        return status === 401 || (status === 403 && tokenType === AccessTokenConsumer.OfficeHome && this.officeHomeApiConfigService.config.jwtAuthEnabled);
    }

    protected isRetriableError(_status: number): boolean {
        return false;
    }

    protected getRetryInterval(): number {
        const index = Math.floor(Math.random() * 1000) % this.retryIntervalsInMs.length;
        return this.retryIntervalsInMs[index];
    }

    protected getRequestIdFromResponse(response: InstrumentedHttpDataResponse<any>, headerName: string = 'request-id'): string {
        return this.getHeaderValue(response.headers, headerName);
    }

    protected getHeaderValue(headers: HttpHeaders, headerName: string): string {
        return headers && headerName && headers.get(headerName);
    }

    /*
     * Update request header and body with auth info
     */
    private updateRequestConfig(requestConfig: HttpRequestOptions, tokenResponse: AccessTokenResponse, updateRequestBody?: (config: HttpRequestOptions, token: AccessTokenResponse) => void) {
        if (!tokenResponse || !tokenResponse.success || !requestConfig) {
            return;
        }

        this.setAuthHeader(requestConfig.headers, tokenResponse);
        if (updateRequestBody) {
            updateRequestBody(requestConfig, tokenResponse);
        }
    }

    private setAuthHeader(headers: Map<string, string | Array<string>>, tokenResponse: AccessTokenResponse): void {
        if (!tokenResponse) {
            return;
        }

        // BUGBUG: Safari does not like headers with trailing space. Have to trim it explicitly
        //         because token can be empty in mock scenarios.
        // TODO: token.lastIndexOf('t=', 0) === 0 is a temporary method to check if this is pwa consumer scenario. Need to find better solution.
        // RPS token requires differing value prefix based on api
        const headerPrefix = tokenResponse.format === AccessTokenFormat.RPSCompact ? this.rpsCompactAuthHeaderPrefix : 'Bearer ';
        headers.set('Authorization', `${headerPrefix}${tokenResponse.token}`.trim());
    }

    private request<T>(
        httpRequest: (config: HttpRequestOptions) => InstrumentedHttpPromise<T>,
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        callback: (promise: InstrumentedHttpPromise<T>) => void,
        errorCallback: (result: XhrServiceError<T>) => void,
        requestConfig: HttpRequestOptions = {},
        tokenConsumerOverride?: AccessTokenConsumer,
        resourceUrl?: string,
        updateRequestBody?: (reqeustConfig: HttpRequestOptions, tokenResponse: AccessTokenResponse) => void,
        fromCache: boolean = false,
        interactive = false,
        capDialogData: CAPDialogData = null
    ) {
        const tokenType = typeof tokenConsumerOverride === 'undefined' ? this.tokenConsumer : tokenConsumerOverride;
        const isOfficeHomeToken = tokenType === AccessTokenConsumer.OfficeHome;
        const apiConfig = this.officeHomeApiConfigService.config;
        const noAuth = tokenType === AccessTokenConsumer.None || (isOfficeHomeToken && !apiConfig.jwtAuthEnabled);
        const finalErrorHandler = (response: InstrumentedHttpDataResponse<T>) => {
            const status = response.status;
            let customErrorCode = Number.NaN;
            if (!noAuth && this.isAuthError(status, tokenType)) {
                customErrorCode = HttpStatusCode.TokenRefreshFailed;
            } else if (this.isRetriableError(status)) {
                customErrorCode = HttpStatusCode.RetryFailed;
            }

            if (customErrorCode !== Number.NaN) {
                this.logCustomError(eventName, otelEventName, customErrorCode, fromCache);
            }

            errorCallback(new XhrServiceError<T>(response));
        };

        const retry = (config, retryAfterMs = 0) => {
            // retry request after delay
            setTimeout(() => {
                const retryPromise = httpRequest(config);
                callback(retryPromise);
                retryPromise.catch(finalErrorHandler);
            }, retryAfterMs || this.getRetryInterval());
        };

        for (const param in this.queryParams) {
            if (param && this.queryParams.hasOwnProperty(param)) {
                const queryParamValue = this.queryParams[param];
                this.handleQueryParam(param.toLowerCase(), queryParamValue, requestConfig);
            }
        }

        if (!requestConfig.headers) {
            requestConfig.headers = new Map<string, string>();
        }

        const tokenPromise = noAuth ? Promise.resolve(null) : this.accessTokenService.getAccessToken(tokenType, false, resourceUrl, interactive, capDialogData);
        tokenPromise
            .then((tokenResponse: AccessTokenResponse) => {
                if (!noAuth) {
                    // If we failed to get the OfficeHome token when using JWT auth on office.com, continue to attempt the request with cookie-based auth instead
                    // of returning an error.
                    // TODO: have service return fresh token and update token cache.
                    if ((!tokenResponse || !tokenResponse.success) &&
                        !(isOfficeHomeToken && apiConfig.jwtAuthEnabled && apiConfig.jwtAuthOnOfficeHome)) {
                        // we were unable to fetch access token; log custom error and call error handler
                        const errorMsg = tokenResponse?.error?.message ?? '';
                        this.logCustomError(eventName, otelEventName, HttpStatusCode.TokenUnavailable, fromCache, errorMsg);

                        errorCallback(new XhrServiceError<T>({
                            data: (errorMsg === 'CAP_Error' ? errorMsg : null) as unknown as T,
                            status: HttpStatusCode.TokenUnavailable,
                            subStatus: tokenResponse?.error?.statusCode,
                            headers: null,
                            config: null,
                            ok: false,
                            statusText: 'Failed to fetch access token',
                            fromCache: false
                        }));
                        return;
                    }

                    // we've obtained an access token, either from a fetch or from the cache
                    this.updateRequestConfig(requestConfig, tokenResponse, updateRequestBody);
                }

                const instrumentedPromise = httpRequest(requestConfig);
                callback(instrumentedPromise);
                instrumentedPromise.catch(response => {
                    if (!noAuth && this.isAuthError(response.status, tokenType)) {
                        const claimsChallenge = this.getClaimsChallenge(response.headers, eventName, otelEventName);
                        // attempt to fetch new access token if unauthorized status code, then retry request if successful
                        this.accessTokenService.getAccessToken(tokenType, true, resourceUrl, interactive, capDialogData, undefined, undefined, undefined, claimsChallenge)
                            .then(freshToken => {
                                if (!freshToken.success) {
                                    finalErrorHandler(response);
                                    return;
                                }
                                this.updateRequestConfig(requestConfig, freshToken, updateRequestBody);
                                const retryPromise = httpRequest(requestConfig);
                                callback(retryPromise);
                                retryPromise.catch(finalErrorHandler);
                            });
                    } else if (this.isRetriableError(response.status)) {
                        const retryAfter = this.getHeaderValue(response.headers, 'Retry-After');
                        const retryAfterSeconds = parseFloat(retryAfter || '0');
                        retry(requestConfig, Math.ceil(retryAfterSeconds * 1000));
                    } else {
                        errorCallback(new XhrServiceError<T>(response));
                    }
                });
            });
    }

    private getClaimsChallenge(responseHeaders: HttpHeaders, eventName: KnownInstrumentationEvents, otelEventName: KnownRequestInstrumentationEvents): string {
        try {
            const wwwAuthHeader = responseHeaders.getAll('WWW-Authenticate')[0];
            if (wwwAuthHeader.includes('insufficient_claims')) {
                const claimsMatch = wwwAuthHeader.match(/[\s|,]*claims=\"(\S+?)\"/);
                if (claimsMatch && claimsMatch.length === 2) {
                    this.logCustomError(eventName, otelEventName, 200, false, 'Found insufficient_claims');
                    return claimsMatch[1];
                }
            }
        } catch (error) {
            this.logCustomError(eventName, otelEventName, 0, false, error);
        }

        return '';
    }

    private logCustomError(
        eventName: KnownInstrumentationEvents,
        otelEventName: KnownRequestInstrumentationEvents,
        errorCode: number,
        fromCache: boolean,
        errorMsg: string = ''
    ) {
        this.instrumentationService.logRequestEvent(eventName, errorCode, -1, errorMsg, fromCache);
        // TODO: Remove above aria logging after OTEL migration
        logRequest({
            eventName: otelEventName,
            fromCache,
            httpCode: errorCode,
            latency: -1,
            error: errorMsg
        });
    }
}
